import { useEffect, useState, useRef, useCallback } from "react";
import useIsProductDeletable from "$hooks/useIsProductDeletable";
import { Company } from "$types";
import { Product } from "$types/Product";

const useDeletableProduct = (product: Product, companyId: Company["_id"]) => {
  const { isDeletable, isLoading: isProductDeletableLoading } =
    useIsProductDeletable();
  const [isProductDeletable, setIsProductDeletable] = useState<boolean>(false);
  const previousProductRef = useRef<string | null>(null);
  const previousCompanyRef = useRef<string | null>(null);
  const checkInProgressRef = useRef<boolean>(false);

  // Reset cache function
  const resetCache = useCallback(() => {
    previousProductRef.current = null;
    previousCompanyRef.current = null;
    setIsProductDeletable(false);
  }, []);

  useEffect(() => {
    // Skip if there's already a check in progress or if product/companyId is missing
    if (checkInProgressRef.current || !product || !companyId) {
      if (!product || !companyId) {
        setIsProductDeletable(false);
      }
      return;
    }

    // Skip if we've already checked this product/company combination
    const productRef = product?.reference;
    if (
      productRef === previousProductRef.current &&
      companyId === previousCompanyRef.current
    ) {
      return;
    }

    const checkIfIsHoveredDeletable = async () => {
      try {
        checkInProgressRef.current = true;
        const result = await isDeletable(productRef, companyId);
        setIsProductDeletable(result);
        // Store references to prevent duplicate calls
        previousProductRef.current = productRef;
        previousCompanyRef.current = companyId;
      } finally {
        checkInProgressRef.current = false;
      }
    };

    checkIfIsHoveredDeletable();
  }, [companyId, isDeletable, product]);

  return {
    isProductDeletable,
    isProductDeletableLoading,
    resetCache,
  };
};

export default useDeletableProduct;
